import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { useCsvExport } from "../../lib/useCsvExport";
import { useTableView } from "../../lib/useTableView";
import { TableToolbar, TablePagination } from "../../components/TableControls";
import { ProductPicker } from "../../components/ProductPicker";
import { useConfirm } from "../../components/ConfirmDialog";
import { firstProblem, minLen, isPositiveAmount, isPositiveInt } from "../../components/validators";
import type { EngineOverviewDto, SalesAction, SalesCustomerDto, ProductCatalogItem, PeriodRange } from "../../ipc/types";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): SalesAction[] {
  if (statusLabel === "Borrador") return ["confirm", "cancel"];
  if (statusLabel === "Confirmada" || statusLabel === "Facturada" || statusLabel === "Despachada") return ["cancel"];
  return [];
}
const ACTION_LABEL: Record<SalesAction, string> = { confirm: "Confirmar", cancel: "Cancelar" };

function NewSaleForm({ customers, catalog, onCreate, onCancel, busy }: {
  customers: readonly SalesCustomerDto[];
  catalog: readonly ProductCatalogItem[];
  onCreate: (v: { customerCode: string; sku: string; description: string; unitPriceMinor: number; quantity: number }) => void;
  onCancel: () => void; busy: boolean;
}): JSX.Element {
  const [customerCode, setCustomerCode] = useState(customers[0]?.code ?? "");
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");
  const priceNum = Number(price);
  const qtyNum = Number(qty);
  const hint = firstProblem([
    { ok: customerCode.length > 0, msg: "Elige un cliente" },
    { ok: minLen(sku, 2), msg: "Elige un producto del catálogo" },
    { ok: isPositiveAmount(price), msg: "El precio debe ser un número mayor que 0" },
    { ok: isPositiveInt(qty), msg: "La cantidad debe ser un entero mayor que 0" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nueva venta" subtitle="Elige un producto de tu catálogo; al confirmar descuenta stock de Inventario" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Cliente</span>
            <select className="pd-input" value={customerCode} aria-label="Cliente" onChange={(e) => setCustomerCode(e.target.value)}>
              {customers.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
            </select></div>
          <div className="pd-field"><span className="pd-field__label">Precio unitario (CLP)</span>
            <input className="pd-input" value={price} type="number" min="0" placeholder="Ej: 54990" aria-label="Precio unitario" onChange={(e) => setPrice(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Cantidad</span>
            <input className="pd-input" value={qty} type="number" min="1" aria-label="Cantidad" onChange={(e) => setQty(e.target.value)} /></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Producto (SKU)</span>
            <ProductPicker catalog={catalog} value={sku} onChange={setSku} ariaLabel="Producto" onPick={(item) => setName(item.name)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ customerCode, sku: sku.trim(), description: name || sku.trim(), unitPriceMinor: Math.round(priceNum), quantity: Math.round(qtyNum) })}>
            {busy ? "Creando…" : "Crear venta"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function SalesScreen(): JSX.Element {
  const client = useIpc();
  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    // Si la comparación está apagada, mandamos un período anterior vacío → sin deltas.
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const { state, reload } = useIpcQuery<EngineOverviewDto>(
    (c) => c.getEngineOverview("sales", periodRange),
    [range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare],
  );
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [customers, setCustomers] = useState<readonly SalesCustomerDto[]>([]);
  const [catalog, setCatalog] = useState<readonly ProductCatalogItem[]>([]);
  const { ask, node } = useConfirm();
  const { exportCsv, exporting, banner } = useCsvExport(state.status === "ready" ? state.data : { title: "ventas", columns: [], rows: [] });
  const view = useTableView(state.status === "ready" ? state.data.rows : []);

  useEffect(() => {
    let active = true;
    client.listSaleCustomers().then((res) => { if (active && res.ok) setCustomers(res.value.items); });
    client.getProductCatalog().then((res) => { if (active && res.ok) setCatalog(res.value.items); });
    return () => { active = false; };
  }, [client]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { customerCode: string; sku: string; description: string; unitPriceMinor: number; quantity: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createSale(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Venta creada para ${v.customerCode}.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: SalesAction): Promise<void> => {
    if (action === "cancel") {
      const ok = await ask({ title: "Cancelar venta", message: "La orden quedará cancelada. Esta acción no se puede deshacer. ¿Continuar?", confirmLabel: "Sí, cancelar", danger: true });
      if (!ok) return;
    }
    setBusy(true); setMsg(null);
    const res = await client.setSaleStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      {node}
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="sales" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Ventas</h1>
              <p className="pagehead__sub">Órdenes de venta. Crea, confirma y cancela; se guardan en tu equipo.</p>
              {data.period ? (
                <div className="row" style={{ gap: 8, marginTop: 4 }}>
                  <Badge tone="brand" dot>{formatRange({ fromEpoch: data.period.fromEpoch, toEpoch: data.period.toEpoch })}</Badge>
                  {compare ? <span className="faint" style={{ fontSize: 12 }}>vs período anterior</span> : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics, ...(data.period ? { periodLabel: formatRange(data.period) } : {}) }} />
            <Button variant="secondary" size="sm" icon="analytics" disabled={exporting || data.rows.length === 0} onClick={exportCsv}>{exporting ? "Exportando…" : "Exportar CSV"}</Button>
            <Button size="sm" icon="sales" disabled={customers.length === 0} onClick={() => setShowForm((s) => !s)}>Nueva venta</Button>
          </div>
        </div>
      </div>

      {banner}

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewSaleForm customers={customers} catalog={catalog} onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Órdenes" subtitle={`${data.rows.length} órdenes`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="sales" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes ventas</div><div className="faint" style={{ fontSize: 13 }}>Trae tus ventas reales desde <b className="strong">Marketplace → “Traer órdenes de ML”</b>, o crea una manual con “Nueva venta”.</div></div>
          ) : (
            <>
            <TableToolbar view={view} searchPlaceholder="Buscar por orden, cliente o estado…" />
            {view.rows.length === 0 ? (
              <div className="statecard"><div className="faint" style={{ fontSize: 13 }}>Ninguna orden coincide con la búsqueda.</div></div>
            ) : (
            <table className="table">
              <thead><tr><th>Orden</th><th>Cliente</th><th>Estado</th><th>Total</th><th>Acciones</th></tr></thead>
              <tbody>
                {view.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td className="strong">{r.amount}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "confirm" ? "secondary" : "ghost"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            <TablePagination view={view} />
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
